// ============================================================================
// BOOKING TRAINEES — facilitator candidates invited to a facilitator training
// booking. Facilitators edit the list; Tier 1 records attendance once the
// session has started, which writes each attendee's training record.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GraduationCap, Pencil, Check, X, Loader, ClipboardCheck } from 'lucide-react';
import {
  listEventTypes,
  listSelectableCandidates,
  updateBookingDetails,
  recordTraineeAttendance,
  SelectableCandidate,
  StudioBooking
} from '../../services/studio-bookings';

export default function BookingTrainees({ booking, canEdit, onUpdated, onError }: {
  booking: StudioBooking;
  canEdit: boolean;
  onUpdated: (booking: StudioBooking) => void;
  onError: (message: string) => void;
}) {
  const [isTraining, setIsTraining] = useState<boolean | null>(booking.traineeCandidateIds?.length ? true : null);
  const [editing, setEditing] = useState(false);
  const [candidates, setCandidates] = useState<SelectableCandidate[]>([]);
  const [selected, setSelected] = useState<string[]>(booking.traineeCandidateIds ?? []);
  const [recording, setRecording] = useState(false);
  const [attended, setAttended] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const { can } = useAuth();
  const isFacilitator = can('Facilitation::NominateCandidates');
  const isTier1OrAdmin = can('Studio::ReviewBookings');
  const trainees = (booking.traineeCandidateIds ?? []).map((id, i) => ({ id, name: booking.traineeNames?.[i] ?? 'Unknown' }));
  const recorded = !!booking.traineeAttendanceRecordedAt;
  const today = new Date().toISOString().slice(0, 10);
  const canRecord = isTier1OrAdmin && booking.status === 'approved' && trainees.length > 0 && !recorded && booking.startDate <= today;

  useEffect(() => {
    if (isTraining !== null) return;
    listEventTypes()
      .then(types => setIsTraining(!!types.find(t => t.eventTypeId === booking.eventTypeId)?.isFacilitatorTraining))
      .catch(() => setIsTraining(false));
  }, [isTraining, booking.eventTypeId]);

  function startEdit() {
    setSelected(booking.traineeCandidateIds ?? []);
    setEditing(true);
    listSelectableCandidates().then(setCandidates).catch(() => onError('Failed to load interested candidates'));
  }

  function toggle(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter(x => x !== id) : [...list, id];
  }

  async function saveTrainees() {
    setSaving(true);
    try {
      onUpdated(await updateBookingDetails(booking.bookingId, { traineeCandidateIds: selected }));
      setEditing(false);
    } catch (err: any) {
      onError(err.response?.data?.error || 'Failed to update trainees');
    } finally {
      setSaving(false);
    }
  }

  async function saveAttendance() {
    if (!window.confirm(`Record ${attended.length} of ${trainees.length} trainees as attended? This writes their training record and can't be redone.`)) return;
    setSaving(true);
    try {
      onUpdated(await recordTraineeAttendance(booking.bookingId, attended));
      setRecording(false);
    } catch (err: any) {
      onError(err.response?.data?.error || 'Failed to record attendance');
    } finally {
      setSaving(false);
    }
  }

  if (!isTraining || (!isFacilitator && trainees.length === 0)) return null;

  // Already-invited trainees stay selectable even if they've since left the list (e.g. certified).
  const options = [
    ...trainees.filter(t => !candidates.some(c => c.candidateId === t.id)).map(t => ({ candidateId: t.id, name: t.name })),
    ...candidates
  ];

  return (
    <div className="flex gap-2.5 text-sm items-start">
      <GraduationCap className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
      {editing ? (
        <div className="flex flex-col gap-2 flex-1">
          <div className="flex flex-col gap-1 max-h-40 overflow-y-auto border border-[#e2e8f0] rounded-lg p-2">
            {options.length === 0 ? (
              <p className="text-xs text-slate-400 px-1.5 py-1">Nobody is on the interested list yet.</p>
            ) : options.map(c => (
              <label key={c.candidateId} className="flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(c.candidateId)}
                  onChange={() => setSelected(prev => toggle(prev, c.candidateId))}
                  className="w-3.5 h-3.5 accent-primary rounded"
                />
                <span className="text-sm text-navy">{c.name}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 self-end">
            <button onClick={saveTrainees} disabled={saving} className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60">
              <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setEditing(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : recording ? (
        <div className="flex flex-col gap-2 flex-1">
          <p className="text-xs text-slate-500">Tick who attended. Each gets an in-person training record dated {booking.startDate}.</p>
          <div className="flex flex-col gap-1 border border-[#e2e8f0] rounded-lg p-2">
            {trainees.map(t => (
              <label key={t.id} className="flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={attended.includes(t.id)}
                  onChange={() => setAttended(prev => toggle(prev, t.id))}
                  className="w-3.5 h-3.5 accent-primary rounded"
                />
                <span className="text-sm text-navy">{t.name}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 self-end">
            <button onClick={saveAttendance} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary/90 disabled:opacity-60">
              {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save attendance
            </button>
            <button onClick={() => setRecording(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {trainees.length > 0 ? trainees.map(t => {
              const went = booking.traineeAttendedIds?.includes(t.id);
              const cls = !recorded ? 'text-slate-500 bg-slate-100'
                : went ? 'text-emerald-700 bg-emerald-50' : 'text-slate-400 bg-slate-50 line-through';
              return <span key={t.id} className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{t.name}</span>;
            }) : (
              <span className="text-slate-400 italic text-sm">No trainees invited</span>
            )}
            {canEdit && isFacilitator && !recorded && (
              <button onClick={startEdit} className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50" title="Edit trainees">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {recorded && <p className="text-xs text-slate-400">Attendance recorded: {booking.traineeAttendedIds?.length ?? 0} of {trainees.length} attended</p>}
          {canRecord && (
            <button
              onClick={() => { setAttended(trainees.map(t => t.id)); setRecording(true); }}
              className="self-start flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
            >
              <ClipboardCheck className="w-3.5 h-3.5" /> Record attendance
            </button>
          )}
        </div>
      )}
    </div>
  );
}
